import { UserContext } from "context/UserContext";
import { loadRecord } from "pages/ALI/ALIRead";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ALIRecordRequest } from "services/ALIRecord";
import { formatPhoneNumber, unformatPhoneNumber } from "utils/helpers";
import { Input } from "./Input";

interface NewCPNInputProps {
  recordRequest: ALIRecordRequest;
  setRecordRequest: React.Dispatch<React.SetStateAction<ALIRecordRequest>>;
  icon?: boolean;
  size?: "s" | "m" | "l";
  placeholder?: boolean;
  focused?: boolean;
  key?: string;
  mode?: "create" | "edit" | "migrate";
  fillOutAdditionalFieldsOnFinish?: boolean;
}

export const NewCPNInput: React.FC<NewCPNInputProps> = ({
  recordRequest,
  setRecordRequest,
  icon = false,
  size = "s",
  placeholder = true,
  focused = false,
  key = undefined,
  mode = "create",
  fillOutAdditionalFieldsOnFinish = false,
}) => {
  const { user } = React.useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [preexistingRecord, setPreexistingRecord] =
    React.useState<boolean>(false);

  const [message, setMessage] = React.useState<
    string | JSX.Element | undefined
  >(undefined);
  const [valid, setValid] = React.useState<boolean>(true);

  useEffect(() => {
    const fetchRecord = async () => {
      setLoading(true);
      let recordExists = false;

      try {
        if (unformatPhoneNumber(recordRequest.CPN).length === 10) {
          const record = await loadRecord({
            params: { cpn: unformatPhoneNumber(recordRequest.CPN) },
          });

          recordExists = !!record;
        }
      } catch (error) {
        recordExists = false;
      }

      setPreexistingRecord(recordExists);
      setLoading(false);

      // only show error message if CPN is valid length to avoid confusion while user is typing
      if (unformatPhoneNumber(recordRequest.CPN).length === 10) {
        if (mode === "create" && recordExists) {
          setValid(false);
          setMessage(
            <p>
              Record exists for this Telephone Number
              {user?.functions?.includes("foresite:ali:write") && (
                <>
                  ,{" "}
                  <a
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "#0073e6",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/ali-records/edit/${unformatPhoneNumber(
                          recordRequest.CPN,
                        )}`,
                      );
                    }}
                    href={`/ali-records/edit/${unformatPhoneNumber(
                      recordRequest.CPN,
                    )}`}
                  >
                    Edit
                  </a>{" "}
                  or{" "}
                  <a
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "#0073e6",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/ali-records/migrate`, {
                        state: { cpn: recordRequest.CPN },
                      });
                    }}
                    href={`/ali-records/migrate`}
                  >
                    Migrate
                  </a>
                </>
              )}
            </p>,
          );
        } else if (mode === "migrate" && recordExists === false) {
          setValid(false);
          setMessage("No existing record to migrate");
        } else {
          setValid(true);
          setMessage(undefined);
        }
      } else {
        setValid(true);
        setMessage(undefined);
      }
    };

    fetchRecord();
  }, [recordRequest.CPN, mode, navigate, preexistingRecord, user?.functions]);

  console.log("Preexisting Record:", preexistingRecord);

  return (
    <Input
      name="CPN"
      label="Telephone Number*"
      value={recordRequest.CPN}
      onChange={(e) => {
        const formattedNumber = formatPhoneNumber(e.target.value);
        setRecordRequest((prev: ALIRecordRequest) => ({
          ...prev,
          CPN: formattedNumber,
        }));

        if (
          fillOutAdditionalFieldsOnFinish &&
          unformatPhoneNumber(formattedNumber).length === 10
        ) {
          setRecordRequest((prev: ALIRecordRequest) => ({
            ...prev,
            MainNPA: unformatPhoneNumber(formattedNumber).substring(0, 3),
            MainNumber: unformatPhoneNumber(formattedNumber).substring(3),
          }));
        } else if (
          fillOutAdditionalFieldsOnFinish &&
          unformatPhoneNumber(formattedNumber).length < 10
        ) {
          setRecordRequest((prev: ALIRecordRequest) => ({
            ...prev,
            MainNPA: "",
            MainNumber: "",
          }));
        }
      }}
      focused={focused}
      max={14}
      scale={size}
      labelSize={size}
      readonly={mode === "edit" ? true : false}
      placeholder={placeholder ? "(XXX) XXX-XXXX" : ""}
      icon={icon ? "phone" : undefined}
      type="tel"
      status={valid ? "valid" : "invalid"}
      key={key}
      message={message ? message : undefined}
    />
  );
};
